import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  deliveriesService, sitesService, suppliersService, beverageProductsService,
  stocktakingSettingsService, stocktakeAttachmentService, supplierProductsService,
} from '../services';
import { useSettings } from '../contexts/SettingsContext';
import ProductPicker from '../components/ProductPicker';
import SitePicker from '../components/SitePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  Save, Loader2, Trash2, Plus, ArrowLeft, CheckCircle2, Undo2,
  Truck, AlertTriangle, Paperclip, Upload, X, FileDown, PackagePlus, PlusCircle,
} from 'lucide-react';
import QuickAddSupplierModal from '../components/QuickAddSupplierModal';
import { getApiError } from '@/lib/utils';
import { getYieldHint } from '@/lib/productHelpers';
import AddFromMasterModal from '../components/AddFromMasterModal';

const emptyLine = () => ({
  beverage_product_id: '', quantity: '', cost_price: '', unit_label: '', notes: '',
});

// iter825 — A line's cost must never be silently replaced by a fresh DN
// cascade lookup once it's either typed by the auditor ('manual') or
// loaded from a saved DN ('saved' — which may itself be a past override).
// Every lookupDnCost call site below checks this before touching cost_price.
const isProtectedCostSource = (source) => source === 'manual' || source === 'saved';

// 2026-11 — Delivery-note auto-save coherence fix.
// After every successful save, the server's echoed line data used to be
// written back into `lines` state with brand-new object/array references,
// even when nothing had actually changed. Because the auto-save effect
// below keys off `[header, lines]`, that reference churn alone was enough
// to re-arm the 2 s debounce and fire another identical save — forever
// ("Auto-saving" / "Unsaved changes" cycling with no edits). Comparing by
// value first lets `setLines` bail out to the SAME reference when the
// echoed data truly matches what's already in state.
const _numEq = (a, b) => {
  const na = parseFloat(a); const nb = parseFloat(b);
  if (Number.isNaN(na) && Number.isNaN(nb)) return true;
  return na === nb;
};
const _linesValueEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((ln, i) => {
    const o = b[i];
    return String(ln.beverage_product_id || '') === String(o.beverage_product_id || '')
      && _numEq(ln.quantity, o.quantity)
      && _numEq(ln.cost_price, o.cost_price)
      && (ln.unit_label || '') === (o.unit_label || '')
      && (ln.notes || '') === (o.notes || '');
  });
};

// Value-based signature of the data that would actually be sent to the
// server. The auto-save effect compares this against the signature taken
// right after load / right after the last successful save, instead of
// relying on object/array reference identity. Reference identity alone was
// the other half of the "opens with no edits and still cycles Auto-saving"
// bug: a duplicate/StrictMode-doubled load response, or any state update
// that merely echoes unchanged data, produces a NEW reference every time,
// which looked exactly like a real edit to the old `[header, lines]` effect.
const _snapshot = (h, ls) => JSON.stringify({
  site_id: h.site_id || '', supplier_id: h.supplier_id || '',
  reference_number: h.reference_number || '', delivery_date: h.delivery_date || '',
  notes: h.notes || '', invoice_total: h.invoice_total ? parseFloat(h.invoice_total) : null,
  lines: (ls || []).map(ln => ({
    beverage_product_id: String(ln.beverage_product_id || ''),
    quantity: parseFloat(ln.quantity) || 0,
    cost_price: parseFloat(ln.cost_price) || 0,
    unit_label: ln.unit_label || '',
    notes: ln.notes || '',
  })),
});

export default function DeliveryEntryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currencySymbol } = useSettings();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [saveAndPosting, setSaveAndPosting] = useState(false);
  const [delivery, setDelivery] = useState(null);

  const [header, setHeader] = useState({
    site_id: '', supplier_id: '', reference_number: '',
    delivery_date: new Date().toISOString().split('T')[0],
    notes: '', invoice_total: '',
  });
  const [lines, setLines] = useState([emptyLine()]);

  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  // Quick-add supplier modal state (Item 2 — float modal over delivery form)
  const [quickAddSupplierOpen, setQuickAddSupplierOpen] = useState(false);

  const [settings, setSettings] = useState({ default_vat_rate: 20, invoice_basis: 'net' });
  const [supplierProducts, setSupplierProducts] = useState(null);
  const [uploading, setUploading] = useState(false);

  // 2026-05-08 (iter340) — Forest Lodge duplication fix.
  // `setSaving(true)` doesn't take effect synchronously, so a fast
  // double-click on Save / Save & Post can fire two simultaneous
  // `handleSave` calls. With `isNew=true`, both calls hit POST /deliveries
  // and create twin records before navigate() updates the URL.
  // This ref blocks re-entry within the same JS tick, regardless of
  // React state batching.
  const saveInFlightRef = useRef(false);
  // iter825 — per-line request counter guarding lookupDnCost against
  // out-of-order async responses (see lookupDnCost below).
  const lookupTokenRef = useRef({});
  // Auto-save state
  const autoSaveTimerRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle'|'pending'|'saving'|'saved'|'error'
  // Set when a debounced auto-save fires while a previous save is still in
  // flight. The edit is queued and re-run the instant the in-flight save
  // finishes, instead of being silently dropped (see runAutoSave below).
  const pendingAutoSaveRef = useRef(false);
  // Value-based signature (see _snapshot) of header+lines as of the last
  // successful load or save. The auto-save effect diffs against this
  // instead of trusting object/array reference identity, so a re-render
  // that merely echoes unchanged data (save response, duplicate load)
  // never masquerades as a real edit.
  const lastSavedSnapshotRef = useRef(null);

  const [addFromMasterOpen, setAddFromMasterOpen] = useState(false);
  const handleProductAssignedFromMaster = async (product) => {
    // Refresh the master products list so the newly-assigned product
    // appears in the picker without a full page reload.
    try {
      const fresh = await beverageProductsService.listAll();
      setProducts(fresh || []);
    } catch {
      // Best-effort; user can refresh manually.
    }
  };

  const isPosted = delivery?.status === 'posted';
  // Posted deliveries can now be edited in-place (save writes lines and re-totals
  // without losing the posted timestamps). This matches the "live correction"
  // workflow the team expects: no unpost step required to fix a typo or add a line.
  // The backend save_delivery_lines endpoint does not enforce draft-only status.
  const isLocked = false;  // reserved for future hard-lock states
  const editDisabled = isLocked;

  // Load lookup data + settings
  useEffect(() => {
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const [sitesData, suppliersData, productsData, settingsData] = await Promise.all([
          sitesService.list({ limit: 500 }),
          suppliersService.list({ limit: 500, is_active: true }),
          beverageProductsService.listAll({ status: 'active' }),
          stocktakingSettingsService.get().catch(() => ({ default_vat_rate: 20, invoice_basis: 'net' })),
        ]);
        if (cancelled) return;
        const sitesArr = Array.isArray(sitesData) ? sitesData : sitesData.sites || sitesData.items || [];
        setSites(sitesArr);
        setSuppliers(suppliersData.items || []);
        setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
        setSettings(settingsData);
      } catch {
        if (!cancelled) {
          await new Promise(r => setTimeout(r, 1000));
          try {
            const [sitesData, suppliersData, productsData] = await Promise.all([
              sitesService.list({ limit: 500 }),
              suppliersService.list({ limit: 500, is_active: true }),
              beverageProductsService.listAll({ status: 'active' }),
            ]);
            if (cancelled) return;
            setSites(Array.isArray(sitesData) ? sitesData : sitesData.sites || sitesData.items || []);
            setSuppliers(suppliersData.items || []);
            setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
          } catch { /* silent on retry */ }
        }
      }
    };
    loadLookups();
    return () => { cancelled = true; };
  }, []);

  // Load existing delivery
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    const loadDelivery = async () => {
      try {
        const d = await deliveriesService.get(Number(id));
        if (cancelled) return;
        setDelivery(d);
        const newHeader = {
          site_id: String(d.site_id || ''),
          supplier_id: String(d.supplier_id || ''),
          reference_number: d.reference_number || '',
          delivery_date: d.delivery_date || '',
          notes: d.notes || '',
          invoice_total: d.invoice_total !== null && d.invoice_total !== undefined ? String(d.invoice_total) : '',
        };
        const newLines = d.lines?.length > 0 ? d.lines.map(ln => ({
          beverage_product_id: String(ln.beverage_product_id),
          quantity: String(ln.quantity),
          cost_price: String(ln.cost_price),
          unit_label: ln.unit_label || '',
          notes: ln.notes || '',
          product_name: ln.product_name || '',
          // iter825/iter826 — a cost loaded from a saved DN (possibly the
          // auditor's own prior override) must never be silently
          // replaced by a fresh cascade lookup just because the site is
          // touched afterwards; 'saved' is treated the same as 'manual'
          // by every lookupDnCost guard below. Only protect it when
          // there's an actual value to protect — a zero/blank saved
          // line should still be eligible for an auto-fill suggestion.
          _cost_source: parseFloat(ln.cost_price) > 0 ? 'saved' : null,
        })) : null;
        setHeader(newHeader);
        if (newLines) setLines(newLines);
        // Record what was just loaded as the "last saved" state so the
        // auto-save effect's value-based dirty check (see _snapshot) treats
        // this load — including a second, StrictMode-doubled load response
        // racing in behind it — as nothing to save, not as an edit.
        lastSavedSnapshotRef.current = _snapshot(newHeader, newLines || lines);
      } catch {
        if (cancelled) return;
        toast({ title: 'Error', description: 'Failed to load delivery', variant: 'destructive' });
        navigate('/stocktaking/deliveries');
      } finally {
        if (cancelled) return;
        // Defer enabling auto-save until after React flushes the initial
        // setHeader / setLines state updates so the auto-save effect does not
        // fire on the first render cycle (which is just the data load).
        setTimeout(() => { if (!cancelled) initialLoadDoneRef.current = true; }, 0);
        setLoading(false);
      }
    };
    loadDelivery();
    // A second request for the same id (StrictMode's deliberate double-invoke
    // in dev, or a fast re-render before the first resolves) must not re-seed
    // state after this one has already settled — `cancelled` blocks that.
    return () => { cancelled = true; };
  }, [id, isNew, navigate, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch supplier-linked products when supplier changes
  useEffect(() => {
    if (!header.supplier_id) {
      setSupplierProducts(null);
      return;
    }
    supplierProductsService.getLinkedProducts(header.supplier_id)
      .then(data => {
        if (data.products?.length > 0) {
          setSupplierProducts(data.products);
        } else {
          setSupplierProducts(null);
        }
      })
      .catch(() => setSupplierProducts(null));
  }, [header.supplier_id]);

  const availableProducts = useMemo(() => {
    if (!supplierProducts) return products;
    const linkedIds = new Set(supplierProducts.map(p => p.beverage_product_id));
    return products.filter(p => linkedIds.has(p.id));
  }, [products, supplierProducts]);

  const getProduct = (productId) => {
    if (!productId) return null;
    return products.find(pr => String(pr.id) === String(productId)) || null;
  };

  const getUnitOptions = (productId) => {
    const prod = getProduct(productId);
    if (!prod || !prod.units?.length) return [];
    const options = prod.units.map(u => {
      let label = u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1);
      if (u.conversion_to_base && u.unit_type !== 'case') {
        label += ` ${u.conversion_to_base}ml`;
      }
      return { value: u.unit_type, label };
    });
    if (prod.case_size && prod.case_size > 0 && !prod.units.some(u => u.unit_type === 'case')) {
      options.push({ value: 'case', label: `Case (${prod.case_size})` });
    }
    const caseIdx = options.findIndex(o => o.value === 'case');
    if (caseIdx >= 0 && prod.case_size) {
      options[caseIdx].label = `Case (${prod.case_size})`;
    }
    return options;
  };

  const getConversionNote = (ln) => {
    const prod = getProduct(ln.beverage_product_id);
    if (!prod || !prod.case_size || prod.case_size <= 0) return null;
    if (ln.unit_label !== 'case') return null;
    const qty = parseFloat(ln.quantity) || 0;
    if (qty <= 0) return null;
    const totalSingles = Math.round(qty * prod.case_size * 100) / 100;
    const fullCases = Math.floor(qty);
    const remainder = Math.round((qty - fullCases) * prod.case_size);
    const singleUnit = prod.units?.find(u => u.unit_type !== 'case')?.unit_type || 'unit';
    const singleLabel = singleUnit.charAt(0).toUpperCase() + singleUnit.slice(1) + (totalSingles !== 1 ? 's' : '');
    if (remainder > 0) {
      return `= ${totalSingles} ${singleLabel} (${fullCases} case${fullCases !== 1 ? 's' : ''} + ${remainder} ${singleUnit}${remainder !== 1 ? 's' : ''})`;
    }
    return `= ${totalSingles} ${singleLabel}`;
  };

  // iter825 — purchase-unit resolution, shared by product-select and
  // unit-toggle so both use the SAME priority: is_default_purchase_unit
  // → is_base_stock_unit → first non-case unit → first unit. When
  // `unitLabel` is given, resolves that exact unit row instead (used
  // when the auditor toggles the unit dropdown).
  const resolvePurchaseUnit = (prod, unitLabel) => {
    const units = prod?.units || [];
    if (unitLabel) return units.find(u => u.unit_type === unitLabel) || null;
    return units.find(u => u.is_default_purchase_unit)
      || units.find(u => u.is_base_stock_unit)
      || units.find(u => u.unit_type !== 'case')
      || units[0] || null;
  };

  // iter825 — Manual DN unit-cost default, per explicit directive:
  // source EXCLUSIVELY from the most recent saved, non-voided DN line
  // (draft or posted) for the SAME site and SAME purchase unit — never
  // WAC, never another site/client, never a credit note, never an
  // incompatible unit, never opening stock. No match -> leave blank for
  // manual entry. Used by product-select, unit-toggle and site-change —
  // any of those three can change which DN line qualifies as a source.
  // A per-line request token guards against an out-of-order response
  // (e.g. a slow site-A lookup resolving after a newer site-B one).
  const lookupDnCost = async (idx, { productId, siteId, unitId, unitLabel, beforeDate }) => {
    if (!productId || !siteId) return;
    const myToken = (lookupTokenRef.current[idx] || 0) + 1;
    lookupTokenRef.current[idx] = myToken;
    let pulled = null;
    let source = null;
    let sourceMeta = null;
    try {
      const dn = await deliveriesService.dnUnitCost(
        Number(productId),
        Number(siteId),
        unitId ? Number(unitId) : undefined,
        unitLabel || undefined,
        beforeDate !== undefined ? (beforeDate || undefined) : (header.delivery_date || undefined),
      );
      if (dn?.cost_price && parseFloat(dn.cost_price) > 0) {
        pulled = String(dn.cost_price);
        source = 'dn_line';
        sourceMeta = dn;
      }
    } catch { /* best-effort — leave blank for manual entry */ }

    // Stale-response guard: a newer lookup has since been issued for
    // this line (site/unit/product changed again while this was in flight).
    if (lookupTokenRef.current[idx] !== myToken) return;

    setLines(prev => prev.map((ln, i) => {
      if (i !== idx) return ln;
      // Don't overwrite a value the user manually typed, or a cost
      // loaded from a saved DN (which may itself be an override).
      if (isProtectedCostSource(ln._cost_source) && ln.cost_price && parseFloat(ln.cost_price) > 0) return ln;
      return {
        ...ln,
        cost_price: pulled || '',
        _cost_source: source, // 'dn_line' | null
        _cost_source_meta: sourceMeta,
      };
    }));
  };

  const handleProductChange = (idx, productId) => {
    const updates = { beverage_product_id: productId, _cost_source: null, _cost_source_meta: null };
    let purchaseUnit = null;
    if (productId) {
      const prod = getProduct(productId);
      purchaseUnit = resolvePurchaseUnit(prod, null);
      if (purchaseUnit?.unit_type) {
        updates.unit_label = purchaseUnit.unit_type;
      }
      // iter340 v8 — Strict cost cascade: clear any auto-pulled cost
      // before lookup so the badge reflects the freshest source.
      updates.cost_price = '';
    }
    setLines(prev => prev.map((ln, i) => i === idx ? { ...ln, ...updates } : ln));

    if (!productId || !header.site_id) return;
    lookupDnCost(idx, { productId, siteId: header.site_id, unitId: purchaseUnit?.id, unitLabel: purchaseUnit?.unit_type });
  };

  // iter825 — Changing the site invalidates every line's auto-filled DN
  // cost: a different site is a different client and must never show
  // another site's cost. Clear any non-manual/non-saved cost immediately,
  // then re-look-up against the new site for every line that already has
  // a product selected. A cost the auditor typed, or loaded from a saved
  // DN (possibly their own prior override), is left untouched.
  const handleSiteChange = (newSiteId) => {
    setHeader(h => ({ ...h, site_id: newSiteId }));
    setLines(prev => prev.map(ln => {
      if (!ln.beverage_product_id || isProtectedCostSource(ln._cost_source)) return ln;
      return { ...ln, cost_price: '', _cost_source: null, _cost_source_meta: null };
    }));
    if (!newSiteId) return;
    lines.forEach((ln, idx) => {
      if (!ln.beverage_product_id || isProtectedCostSource(ln._cost_source)) return;
      const prod = getProduct(ln.beverage_product_id);
      const unit = resolvePurchaseUnit(prod, ln.unit_label || null);
      lookupDnCost(idx, { productId: ln.beverage_product_id, siteId: newSiteId, unitId: unit?.id, unitLabel: ln.unit_label || unit?.unit_type });
    });
  };

  // iter825 — The delivery date is the `before_date` boundary for "most
  // recent" (a backdated DN must never be out-matched by one dated
  // later than it). Changing the date after lines are already filled
  // must re-run the lookup for every non-protected line, same as a
  // site change.
  const handleDeliveryDateChange = (newDate) => {
    setHeader(h => ({ ...h, delivery_date: newDate }));
    if (!header.site_id) return;
    // iter826 — clear immediately (matches handleSiteChange) so a
    // stale cost/badge from the old date never lingers while the
    // fresh lookup is in flight.
    setLines(prev => prev.map(ln => {
      if (!ln.beverage_product_id || isProtectedCostSource(ln._cost_source)) return ln;
      return { ...ln, cost_price: '', _cost_source: null, _cost_source_meta: null };
    }));
    lines.forEach((ln, idx) => {
      if (!ln.beverage_product_id || isProtectedCostSource(ln._cost_source)) return;
      const prod = getProduct(ln.beverage_product_id);
      const unit = resolvePurchaseUnit(prod, ln.unit_label || null);
      lookupDnCost(idx, { productId: ln.beverage_product_id, siteId: header.site_id, unitId: unit?.id, unitLabel: ln.unit_label || unit?.unit_type, beforeDate: newDate });
    });
  };

  const linesTotal = useMemo(() => {
    return lines.reduce((sum, ln) => {
      const qty = parseFloat(ln.quantity) || 0;
      const cost = parseFloat(ln.cost_price) || 0;
      return sum + qty * cost;
    }, 0);
  }, [lines]);

  const invoiceTotal = parseFloat(header.invoice_total) || 0;
  const vatRate = (settings.default_vat_rate || 0) / 100;
  const invoiceBasis = settings.invoice_basis || 'net';

  const vatBreakdown = useMemo(() => {
    if (invoiceBasis === 'gross') {
      const gross = linesTotal;
      const net = gross / (1 + vatRate);
      const vat = gross - net;
      return { net, vat, gross };
    }
    const net = linesTotal;
    const vat = net * vatRate;
    const gross = net + vat;
    return { net, vat, gross };
  }, [linesTotal, vatRate, invoiceBasis]);

  const variance = header.invoice_total ? invoiceTotal - vatBreakdown.gross : 0;

  const updateLine = (idx, field, value) => {
    const prevLine = lines[idx];
    setLines(prev => prev.map((ln, i) => {
      if (i !== idx) return ln;
      const next = { ...ln, [field]: value };
      // iter340 v8 — flip the cost-source indicator to "manual" the
      // moment the user types in the cost field, so the auditor knows
      // the figure no longer reflects last-delivery / opening-stock
      // pulls.
      if (field === 'cost_price') next._cost_source = value && parseFloat(value) > 0 ? 'manual' : null;
      // iter825/iter826 — a cost is always priced FOR a specific unit, so
      // toggling the unit (e.g. case → bottle) invalidates ANY previous
      // figure for this line — manual, saved or cascaded alike. Clearing
      // unconditionally here prevents a case price from silently being
      // auto-saved against a bottle unit (and later poisoning a future
      // lookup at the wrong per-unit price). A fresh lookup for the new
      // unit follows below; the auditor can still retype it.
      if (field === 'unit_label') {
        next.cost_price = '';
        next._cost_source = null;
        next._cost_source_meta = null;
      }
      return next;
    }));
    if (field === 'unit_label' && value && prevLine && prevLine.beverage_product_id && header.site_id) {
      const prod = getProduct(prevLine.beverage_product_id);
      const unit = resolvePurchaseUnit(prod, value || null);
      lookupDnCost(idx, { productId: prevLine.beverage_product_id, siteId: header.site_id, unitId: unit?.id, unitLabel: value });
    }
  };
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const getProductName = (prodId) => {
    const p = products.find(pr => String(pr.id) === String(prodId));
    return p ? `${p.product_code ? p.product_code + ' - ' : ''}${p.product_name}` : '';
  };

  // Attachment
  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !delivery?.id) return;
    setUploading(true);
    try {
      const result = await stocktakeAttachmentService.uploadDelivery(delivery.id, file);
      setDelivery(prev => ({ ...prev, attachment_url: result.attachment_url, attachment_filename: result.attachment_filename }));
      toast({ title: 'Uploaded', description: 'Attachment uploaded successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to upload attachment', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleAttachmentDownload = async () => {
    if (!delivery?.id) return;
    try {
      const response = await stocktakeAttachmentService.downloadDeliveryAttachment(delivery.id);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast({ title: 'Error', description: 'Failed to download attachment', variant: 'destructive' });
    }
  };

  const handleAttachmentRemove = async () => {
    if (!delivery?.id) return;
    try {
      await stocktakeAttachmentService.removeDelivery(delivery.id);
      setDelivery(prev => ({ ...prev, attachment_url: null, attachment_filename: null }));
      toast({ title: 'Removed', description: 'Attachment removed' });
    } catch {
      toast({ title: 'Error', description: 'Failed to remove attachment', variant: 'destructive' });
    }
  };

  // Shared status transition after an auto-save attempt resolves, used by
  // both the normal 2 s debounce path (runAutoSave) and the queued-retry
  // path inside handleSave's own `finally` (below) — so a collision caused
  // by a manual Save click, another auto-save, or the navigate-away/unmount
  // flush all converge on the same, correct status handling.
  const applyAutoSaveResult = (result) => {
    if (result === 'queued') {
      // Another save picked this up for us; it is still in flight (or about
      // to retry) — stay on "pending", don't flash "error".
      setAutoSaveStatus('pending');
    } else if (result !== null) {
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(s => s === 'saved' ? 'idle' : s), 3000);
    } else {
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus(s => s === 'error' ? 'idle' : s), 5000);
    }
  };

  // Save — returns deliveryId for chaining, 'queued' if deferred behind an
  // in-flight save, or null on failure.
  // isAutoSave=true: suppress toasts, spinner, and validation errors (silent background save)
  const handleSave = async (showToast = true, isAutoSave = false) => {
    // Synchronous duplicate-fire guard (Forest Lodge fix).
    //
    // 2026-11 fix — a blocked call used to just `return null` and vanish.
    // That silently dropped whichever edit triggered it: if a line edit's
    // auto-save was already mid-flight when the user then changed the
    // invoice total, THIS call (carrying the new invoice total) would hit
    // this guard and be discarded — the in-flight call's own success only
    // ever echoed what WAS sent (the line edit), so the invoice total
    // never reached the server. Now it's queued: the in-flight call's
    // `finally` below checks this flag and immediately retries with
    // whatever is current by the time it finishes.
    if (saveInFlightRef.current) {
      // Only queue a retry for an EXISTING draft. For a brand-new delivery,
      // a blocked second call is a duplicate double-click with nothing new
      // to say (the Forest Lodge fix) — queuing it here would risk a second
      // `POST /deliveries` once `isNew` flips to false mid-retry, after the
      // first call's `navigate()` lands. Just drop it, as before.
      if (!isNew) {
        pendingAutoSaveRef.current = true;
        if (isAutoSave) setAutoSaveStatus('pending');
        return 'queued';
      }
      return null;
    }
    saveInFlightRef.current = true;
    // 2026-11 fix (2nd pass) — an auto-save call (from the debounce timer or
    // a queued retry) can arrive after the data it would send has already
    // been persisted by something else in the meantime (a manual Save that
    // landed first, or an earlier retry in the same collision). Skip the
    // round trip entirely rather than sending an identical PUT/PUT pair —
    // this is what was making a manual-Save-during-debounce collision, or a
    // rapid double-Save, cost two full save round-trips instead of one.
    if (isAutoSave && lastSavedSnapshotRef.current !== null && _snapshot(header, lines) === lastSavedSnapshotRef.current) {
      saveInFlightRef.current = false;
      return delivery?.id ?? Number(id);
    }
    if (!header.site_id) {
      saveInFlightRef.current = false;
      if (!isAutoSave) toast({ title: 'Validation', description: 'Please select a site', variant: 'destructive' });
      return null;
    }
    if (!header.delivery_date) {
      saveInFlightRef.current = false;
      if (!isAutoSave) toast({ title: 'Validation', description: 'Please enter a delivery date', variant: 'destructive' });
      return null;
    }
    const validLines = lines.filter(ln => ln.beverage_product_id && parseFloat(ln.quantity));
    if (validLines.length === 0) {
      saveInFlightRef.current = false;
      if (!isAutoSave) toast({ title: 'Validation', description: 'Add at least one line item', variant: 'destructive' });
      return null;
    }

    if (!isAutoSave) setSaving(true);
    try {
      const headerPayload = {
        site_id: Number(header.site_id),
        supplier_id: header.supplier_id ? Number(header.supplier_id) : null,
        reference_number: header.reference_number || null,
        delivery_date: header.delivery_date,
        notes: header.notes || null,
        invoice_total: header.invoice_total ? parseFloat(header.invoice_total) : null,
      };

      let deliveryId;
      if (isNew) {
        const created = await deliveriesService.create(headerPayload);
        deliveryId = created.id;
      } else {
        await deliveriesService.update(Number(id), headerPayload);
        deliveryId = Number(id);
      }

      // Bug fix (2026-04-20, Issue 2): always resolve and send `unit_id`
      // when the product's units list is available. Label-only matching
      // on the backend silently fails when the stored label drifts and
      // causes the saved `stock_quantity` to diverge from what the user
      // typed — an edit appears to "not sustain".
      const linePayloads = validLines.map(ln => {
        const prod = products.find(p => p.id === Number(ln.beverage_product_id));
        const resolvedUnit = prod?.units?.find(
          u => (u.unit_type || '').trim().toLowerCase() === (ln.unit_label || '').trim().toLowerCase()
        );
        return {
          beverage_product_id: Number(ln.beverage_product_id),
          quantity: parseFloat(ln.quantity),
          cost_price: parseFloat(ln.cost_price) || 0,
          unit_label: ln.unit_label || null,
          unit_id: resolvedUnit ? resolvedUnit.id : null,
          notes: ln.notes || null,
        };
      });
      const updated = await deliveriesService.saveLines(deliveryId, linePayloads);
      setDelivery(updated);
      // `header`/`lines` here are exactly what was just submitted (this
      // closure's values never change mid-call) — record that as the new
      // "last saved" baseline for the auto-save effect's dirty check.
      lastSavedSnapshotRef.current = _snapshot(header, lines);

      if (showToast) toast({ title: 'Saved', description: 'Delivery saved successfully' });

      if (isNew) {
        navigate(`/stocktaking/deliveries/${deliveryId}`, { replace: true });
      } else if (updated.lines?.length > 0) {
        const freshLines = updated.lines.map(ln => ({
          beverage_product_id: String(ln.beverage_product_id),
          quantity: String(ln.quantity),
          cost_price: String(ln.cost_price),
          unit_label: ln.unit_label || '',
          notes: ln.notes || '',
          // Preserve product_name so the picker doesn't lose the label after autosave
          product_name: ln.product_name || '',
        }));
        // Bail out to the SAME array reference when the echoed data matches
        // what's already in state — otherwise this write alone re-arms the
        // auto-save debounce below and the save repeats forever with no
        // real edit.
        setLines(prev => (_linesValueEqual(prev, freshLines) ? prev : freshLines));
      }
      return deliveryId;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === 'cross_session_duplicate') {
        if (!isAutoSave) {
          toast({
            title: 'Duplicate Delivery Blocked',
            description: detail.message || `Reference '${header.reference_number}' was already posted for this site in a previous stocktake.`,
            variant: 'destructive',
            duration: 8000,
          });
        }
      } else if (!isAutoSave) {
        toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
      }
      return null;
    } finally {
      if (!isAutoSave) setSaving(false);
      saveInFlightRef.current = false;
      // 2026-11 fix — drain the queue here, in ONE place, regardless of who
      // got blocked (another auto-save tick, a manual Save click, or the
      // navigate-away/unmount flush). Using handleSaveRef (not `handleSave`)
      // means this retry always uses whatever header/lines are current NOW,
      // not whatever this particular call started with.
      if (pendingAutoSaveRef.current) {
        pendingAutoSaveRef.current = false;
        setAutoSaveStatus('saving');
        handleSaveRef.current(false, true).then(applyAutoSaveResult);
      }
    }
  };

  // Mirrors the latest `handleSave` closure so deferred/async callers (the
  // debounce timeout below, its queued retry, and the unmount cleanup)
  // always save the CURRENT header/lines — not whatever was in scope when
  // their effect last ran. Without this, the unmount-save cleanup (further
  // below, registered once with an empty dependency array) only ever saw
  // the pristine, pre-load empty state and silently no-op'd.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // Auto-save: fires 2 s after the last change to header or lines (existing deliveries only).
  // Reduced from 6 s to narrow the window where unsaved data can be lost on
  // navigation (iter594: delivery price / data-loss reports).
  const runAutoSave = async () => {
    setAutoSaveStatus('saving');
    applyAutoSaveResult(await handleSaveRef.current(false, true));
  };
  useEffect(() => {
    if (!initialLoadDoneRef.current || isNew) return;
    // Value-based dirty check — only (re)start the debounce when the data
    // that would actually be sent has genuinely changed since the last
    // load/save. Without this, any state update that merely echoes
    // unchanged data (a duplicate/StrictMode-doubled load response, or the
    // save-success echo above) still produces a brand-new object/array
    // reference, which looked exactly like a real edit and re-armed this
    // effect forever — "Auto-saving…" / "Unsaved changes" cycling on a
    // draft nobody had touched.
    const snap = _snapshot(header, lines);
    if (snap === lastSavedSnapshotRef.current) return;
    setAutoSaveStatus('pending');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { runAutoSave(); }, 2000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [header, lines]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation guard (iter594) ──────────────────────────────────────────
  // Prevent silent data loss when the user navigates away before autosave
  // completes, or when a NEW delivery has unsaved entries.

  // 1) Browser close / hard-refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasWork = isNew
        ? lines.some(l => l.beverage_product_id)
        : autoSaveStatus === 'pending' || autoSaveStatus === 'saving';
      if (hasWork) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [autoSaveStatus, isNew, lines]);

  // 2) React-Router in-app navigation: fire-and-forget save on unmount.
  // useBlocker requires a data router (createBrowserRouter); the app uses
  // BrowserRouter, so we fire saves in the cleanup instead. The HTTP request
  // completes in the browser background regardless of component lifecycle.
  const autoSaveStatusRef = useRef(autoSaveStatus);
  autoSaveStatusRef.current = autoSaveStatus;
  const deliveryIdRef = useRef(delivery?.id);
  deliveryIdRef.current = delivery?.id;
  useEffect(() => {
    return () => {
      if (!deliveryIdRef.current) return;
      if (autoSaveStatusRef.current === 'pending' || autoSaveStatusRef.current === 'saving') {
        // handleSaveRef always holds the CURRENT render's handleSave closure
        // (current header/lines). A plain `handleSave` reference here would
        // always be the one captured when this effect first mounted —
        // i.e. the pristine, pre-load empty state — and would silently
        // no-op on its own validation check.
        //
        // 2026-11 fix — if a save is ALREADY in flight at the moment of
        // unmount, this call used to just bail out (saveInFlightRef guard)
        // with nothing to pick up the edit afterwards. It now queues via
        // the same pendingAutoSaveRef mechanism as any other collision, so
        // the in-flight call's own `finally` flushes the latest data even
        // after this component has unmounted (the fetch/axios request is
        // not tied to React's lifecycle).
        handleSaveRef.current(false, true).catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Post
  const handlePost = async () => {
    if (!delivery?.id) return;
    if (!window.confirm('Post this delivery? Cost reconciliation will be applied if invoice total differs from line totals.')) return;
    setPosting(true);
    try {
      const updated = await deliveriesService.post(delivery.id);
      setDelivery(updated);
      if (updated.lines?.length > 0) {
        setLines(updated.lines.map(ln => ({
          beverage_product_id: String(ln.beverage_product_id),
          quantity: String(ln.quantity),
          cost_price: String(ln.cost_price),
          unit_label: ln.unit_label || '',
          notes: ln.notes || '',
        })));
      }
      if (updated.invoice_total !== null && updated.invoice_total !== undefined) {
        setHeader(h => ({ ...h, invoice_total: String(updated.invoice_total) }));
      }
      toast({ title: 'Posted', description: 'Delivery posted. Cost reconciliation applied.' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to post'), variant: 'destructive' });
    } finally {
      setPosting(false);
    }
  };

  // Save & Post
  const handleSaveAndPost = async () => {
    if (!window.confirm('Save and post this delivery?')) return;
    setSaveAndPosting(true);
    try {
      const savedId = await handleSave(false);
      if (!savedId) { setSaveAndPosting(false); return; }
      const updated = await deliveriesService.post(savedId);
      setDelivery(updated);
      if (updated.lines?.length > 0) {
        setLines(updated.lines.map(ln => ({
          beverage_product_id: String(ln.beverage_product_id),
          quantity: String(ln.quantity),
          cost_price: String(ln.cost_price),
          unit_label: ln.unit_label || '',
          notes: ln.notes || '',
        })));
      }
      toast({ title: 'Saved & Posted', description: 'Delivery saved and posted successfully' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save & post'), variant: 'destructive' });
    } finally {
      setSaveAndPosting(false);
    }
  };

  // Unpost
  const handleUnpost = async () => {
    if (!delivery?.id) return;
    if (!window.confirm('Unpost this delivery? It will return to Draft status.')) return;
    setPosting(true);
    try {
      const updated = await deliveriesService.unpost(delivery.id);
      setDelivery(updated);
      toast({ title: 'Unposted', description: 'Delivery returned to Draft' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to unpost'), variant: 'destructive' });
    } finally {
      setPosting(false);
    }
  };

  // Delete — allowed in any status. Warns the user about audit impact.
  const handleDelete = async () => {
    if (!delivery?.id) return;
    const postedWithLines = delivery.status === 'posted' && (lines?.length || 0) > 0;
    const warning = postedWithLines
      ? 'This will permanently delete a POSTED delivery with line items.\n\nAll downstream stocktake audits that include this delivery will lose these figures on their next recalculation.\n\nContinue?'
      : 'Delete this delivery? This cannot be undone.';
    if (!window.confirm(warning)) return;
    setPosting(true);
    try {
      await deliveriesService.delete(delivery.id);
      toast({
        title: 'Delivery deleted',
        description: postedWithLines ? 'Posted delivery and its line items have been removed.' : 'Delivery removed.',
      });
      navigate('/stocktaking/deliveries');
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const vatLabel = invoiceBasis === 'gross' ? 'incl. VAT' : 'excl. VAT';
  const isBusy = saving || posting || saveAndPosting;

  return (
    <div className="space-y-6" data-testid="delivery-entry-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/deliveries')}
            data-testid="back-to-register">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Truck className="h-6 w-6 text-slate-600" />
              {isNew ? 'New Delivery' : `Delivery #${id}`}
            </h1>
            {delivery && (
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant={isPosted ? 'default' : 'secondary'}
                  className={isPosted ? 'bg-emerald-100 text-emerald-800' : ''}
                >
                  {isPosted ? 'Posted' : 'Draft'}
                </Badge>
                {delivery.posted_by_name && (
                  <span className="text-xs text-muted-foreground">
                    Posted by {delivery.posted_by_name}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
          {isPosted ? (
            <>
              <Button onClick={() => handleSave(true)} disabled={isBusy} variant="outline" data-testid="save-delivery-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save changes
              </Button>
              <Button onClick={handleUnpost} disabled={isBusy} variant="outline"
                data-testid="unpost-delivery-btn">
                {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Undo2 className="h-4 w-4 mr-2" />}
                Unpost
              </Button>
              <Button onClick={handleDelete} disabled={isBusy} variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                data-testid="delete-delivery-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => handleSave(true)} disabled={isBusy} variant="outline" data-testid="save-delivery-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
              <Button onClick={handleSaveAndPost} disabled={isBusy}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-post-delivery-btn">
                {saveAndPosting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save & Post
              </Button>
              <Button onClick={handleDelete} disabled={isBusy || !id} variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                data-testid="delete-delivery-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </>
          )}
          </div>
          {!isNew && autoSaveStatus !== 'idle' && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="auto-save-indicator">
              {autoSaveStatus === 'pending' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
              {autoSaveStatus === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
              {autoSaveStatus === 'saved' && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
              {autoSaveStatus === 'error' && <AlertTriangle className="h-3 w-3 text-amber-500" />}
              <span>
                {autoSaveStatus === 'pending' && 'Unsaved changes'}
                {autoSaveStatus === 'saving' && 'Auto-saving…'}
                {autoSaveStatus === 'saved' && 'Auto-saved'}
                {autoSaveStatus === 'error' && 'Auto-save failed — save manually'}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Delivery Header Form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Delivery Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Site *</Label>
              <SitePicker
                sites={sites}
                value={header.site_id}
                onChange={handleSiteChange}
                disabled={editDisabled}
                testId="delivery-site-select"
              />
            </div>
            <div className="space-y-2">
              <Label>Supplier</Label>
              <div className="flex items-center gap-2">
                <Select value={header.supplier_id || 'none'} onValueChange={(v) => setHeader(h => ({ ...h, supplier_id: v === 'none' ? '' : v }))}
                  disabled={editDisabled}>
                  <SelectTrigger data-testid="delivery-supplier-select" className="flex-1">
                    <SelectValue placeholder="Select supplier..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No supplier --</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.supplier_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!editDisabled && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0 h-9 w-9"
                    title="Add new supplier"
                    onClick={() => setQuickAddSupplierOpen(true)}
                    data-testid="quick-add-supplier-btn"
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {supplierProducts && (
                <p className="text-xs text-blue-600">
                  Showing {availableProducts.length} products linked to this supplier
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Delivery Date *</Label>
              <Input type="date" value={header.delivery_date}
                onChange={(e) => handleDeliveryDateChange(e.target.value)}
                disabled={editDisabled} data-testid="delivery-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Reference / Invoice No.</Label>
              <Input value={header.reference_number}
                onChange={(e) => setHeader(h => ({ ...h, reference_number: e.target.value }))}
                placeholder="e.g. INV-12345" disabled={editDisabled}
                data-testid="delivery-reference-input" />
            </div>
            <div className="space-y-2">
              <Label>Invoice Total ({currencySymbol}) <span className="text-xs text-muted-foreground ml-1">incl. VAT</span></Label>
              <Input type="number" step="0.01" value={header.invoice_total}
                onChange={(e) => setHeader(h => ({ ...h, invoice_total: e.target.value }))}
                placeholder="Optional" disabled={editDisabled}
                data-testid="delivery-invoice-total-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={header.notes}
                onChange={(e) => setHeader(h => ({ ...h, notes: e.target.value }))}
                placeholder="Optional notes" disabled={editDisabled}
                data-testid="delivery-notes-input" />
            </div>
          </div>

          {/* Attachment */}
          {!isNew && (
            <div className="mt-4 pt-4 border-t">
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-2">
                <Paperclip className="h-3.5 w-3.5" /> Document Attachment
              </Label>
              {delivery?.attachment_url ? (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                  <FileDown className="h-4 w-4 text-blue-600 shrink-0" />
                  <button onClick={handleAttachmentDownload}
                    className="text-sm text-blue-600 hover:underline truncate text-left" data-testid="attachment-link">
                    {delivery.attachment_filename || 'Download attachment'}
                  </button>
                  {true && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto shrink-0"
                      onClick={handleAttachmentRemove} data-testid="remove-attachment-btn">
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              ) : !isPosted ? (
                <div className="relative">
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleAttachmentUpload} disabled={uploading}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    data-testid="attachment-input" />
                  <Button variant="outline" size="sm" disabled={uploading} className="pointer-events-none">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    {uploading ? 'Uploading...' : 'Attach invoice / document'}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No attachment</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Line Items</CardTitle>
            <div className="flex items-center gap-2">
              {/* iter340 v8 — Add/Delete enabled on posted deliveries
                  for live correction (backend save_delivery_lines does
                  delete-and-recreate; no draft-only enforcement). */}
              <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Line
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead className="min-w-[280px]">Product</TableHead>
                  <TableHead className="w-[100px]">Qty</TableHead>
                  <TableHead className="w-[140px]">Purchase Unit</TableHead>
                  <TableHead className="w-[120px]">Cost ({currencySymbol}) <span className="text-[10px] font-normal text-muted-foreground block">{vatLabel}</span></TableHead>
                  <TableHead className="w-[120px] text-right">Line Total</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((ln, idx) => {
                  const qty = parseFloat(ln.quantity) || 0;
                  const cost = parseFloat(ln.cost_price) || 0;
                  const lineTotal = qty * cost;
                  const unitOptions = getUnitOptions(ln.beverage_product_id);
                  const conversionNote = getConversionNote(ln);
                  const yieldHint = getYieldHint(getProduct(ln.beverage_product_id));
                  return (
                    <TableRow key={idx} data-testid={`line-row-${idx}`}>
                      <TableCell className="text-muted-foreground text-sm">{idx + 1}</TableCell>
                      <TableCell>
                        {false ? (
                          <span className="text-sm">{getProductName(ln.beverage_product_id) || ln.product_name || '-'}</span>
                        ) : (
                          <ProductPicker
                            products={availableProducts}
                            value={ln.beverage_product_id}
                            onChange={(val) => handleProductChange(idx, val)}
                            disabled={editDisabled}
                            placeholder="Search product..."
                            testId={`line-product-${idx}`}
                          />
                        )}
                        {yieldHint && (
                          <p className="text-[10px] text-purple-600 mt-0.5 font-medium" data-testid={`line-yield-${idx}`}>{yieldHint}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" className="h-9 text-sm"
                          value={ln.quantity}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                          disabled={editDisabled} data-testid={`line-qty-${idx}`} />
                      </TableCell>
                      <TableCell>
                        {false ? (
                          <span className="text-sm capitalize">{ln.unit_label || '-'}</span>
                        ) : (
                          <div>
                            {unitOptions.length > 0 ? (
                              <Select value={ln.unit_label || 'none'}
                                onValueChange={(v) => updateLine(idx, 'unit_label', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-9 text-sm" data-testid={`line-unit-${idx}`}>
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">--</SelectItem>
                                  {unitOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input placeholder="Unit" value={ln.unit_label}
                                onChange={(e) => updateLine(idx, 'unit_label', e.target.value)}
                                className="h-9 text-sm" data-testid={`line-unit-${idx}`} />
                            )}
                            {conversionNote && (
                              <p className="text-[10px] text-blue-600 mt-0.5 font-medium">{conversionNote}</p>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" className="h-9 text-sm"
                          value={ln.cost_price}
                          onChange={(e) => updateLine(idx, 'cost_price', e.target.value)}
                          disabled={editDisabled} data-testid={`line-cost-${idx}`} />
                        {ln._cost_source && ln._cost_source !== 'saved' && (
                          <p
                            className={`text-[10px] mt-0.5 font-medium ${ln._cost_source === 'dn_line' ? 'text-emerald-600' : 'text-slate-500'}`}
                            data-testid={`line-cost-source-${idx}`}
                            title={ln._cost_source_meta ? `Delivery Note #${ln._cost_source_meta.reference_number || ln._cost_source_meta.delivery_id} on ${ln._cost_source_meta.delivery_date} (${ln._cost_source_meta.delivery_status}) — this site only, same unit` : undefined}
                          >
                            {ln._cost_source === 'dn_line' && ln._cost_source_meta
                              ? `from DN #${ln._cost_source_meta.reference_number || ln._cost_source_meta.delivery_id} (${ln._cost_source_meta.delivery_date}, ${ln._cost_source_meta.delivery_status})`
                              : 'manual'}
                          </p>
                        )}
                        {!ln._cost_source && ln.beverage_product_id && (!ln.cost_price || parseFloat(ln.cost_price) <= 0) && (
                          <p className="text-[10px] mt-0.5 font-medium text-amber-600" data-testid={`line-cost-source-${idx}`}>
                            no prior cost — enter manually
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {currencySymbol}{lineTotal.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => removeLine(idx)}
                          disabled={lines.length <= 1}
                          data-testid={`remove-line-${idx}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Totals with VAT breakdown */}
          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Lines Total <span className="text-xs">({vatLabel})</span>
                </span>
                <span className="font-mono font-medium">{currencySymbol}{linesTotal.toFixed(2)}</span>
              </div>
              {vatRate > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      VAT ({settings.default_vat_rate}%)
                    </span>
                    <span className="font-mono text-muted-foreground">{currencySymbol}{vatBreakdown.vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {invoiceBasis === 'gross' ? 'Net' : 'Gross'} Total
                    </span>
                    <span className="font-mono text-muted-foreground">
                      {currencySymbol}{invoiceBasis === 'gross' ? vatBreakdown.net.toFixed(2) : vatBreakdown.gross.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
              {header.invoice_total && (
                <>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Invoice Total</span>
                    <span className="font-mono font-medium">{currencySymbol}{invoiceTotal.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between text-sm font-medium ${Math.abs(variance) > 0.01 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    <span className="flex items-center gap-1">
                      {Math.abs(variance) > 0.01 && <AlertTriangle className="h-3.5 w-3.5" />}
                      Variance
                    </span>
                    <span className="font-mono">
                      {variance >= 0 ? '+' : ''}{currencySymbol}{variance.toFixed(2)}
                    </span>
                  </div>
                  {Math.abs(variance) > 0.01 && !isPosted && (
                    <p className="text-xs text-muted-foreground">
                      On posting, the variance will be spread proportionally across line costs.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <AddFromMasterModal
        open={addFromMasterOpen}
        onOpenChange={setAddFromMasterOpen}
        siteId={header.site_id ? Number(header.site_id) : null}
        assignedProductIds={products.map(p => p.id)}
        onAssigned={handleProductAssignedFromMaster}
      />
      {/* Quick-add supplier modal — floats over the delivery form.
          Delivery header, lines, values and scroll position are fully
          preserved because we only toggle a boolean; no parent state
          is cleared on open, cancel, or error. On successful creation
          the new supplier is appended to the list and auto-selected. */}
      <QuickAddSupplierModal
        open={quickAddSupplierOpen}
        onOpenChange={setQuickAddSupplierOpen}
        onCreated={(newSupplier) => {
          setSuppliers(prev => {
            const already = prev.some(s => s.id === newSupplier.id);
            return already ? prev : [...prev, newSupplier];
          });
          setHeader(h => ({ ...h, supplier_id: String(newSupplier.id) }));
        }}
      />
    </div>
  );
}
